import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useLearningStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/learning/status`, {
        headers: authHeaders(),
      });
      setStatus(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load learning status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { status, loading, error, refresh };
}

export function useLearningCourses(options = {}) {
  const { status: statusFilter, partnerOnly } = options;
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/api/learning/courses`, {
        headers: authHeaders(),
        params,
      });
      let items = data.items || [];
      if (partnerOnly === true) {
        items = items.filter((c) => c.partner_id);
      } else if (partnerOnly === false) {
        items = items.filter((c) => !c.partner_id);
      }
      setCourses(items);
      setTotal(partnerOnly != null ? items.length : data.total ?? items.length);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load courses');
      setCourses([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, partnerOnly]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { courses, total, loading, error, refresh };
}

export function useLearningCourse(courseId) {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(Boolean(courseId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/learning/courses/${courseId}`, {
        headers: authHeaders(),
      });
      setCourse(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load course');
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveCourse = useCallback(
    async (payload) => {
      setSaving(true);
      setError(null);
      try {
        const { data } = await axios.put(`${API}/api/learning/courses/${courseId}`, payload, {
          headers: authHeaders(),
        });
        setCourse(data);
        return data;
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || 'Failed to save course';
        setError(msg);
        throw new Error(msg);
      } finally {
        setSaving(false);
      }
    },
    [courseId]
  );

  const publishCourse = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/learning/courses/${courseId}/publish`,
        {},
        { headers: authHeaders() }
      );
      setCourse(data);
      return data;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to publish course';
      setError(msg);
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  }, [courseId]);

  const unpublishCourse = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/learning/courses/${courseId}/unpublish`,
        {},
        { headers: authHeaders() }
      );
      setCourse(data);
      return data;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to unpublish course';
      setError(msg);
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  }, [courseId]);

  const unlockCourseForEdit = useCallback(async ({ reason, notes }) => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/learning/courses/${courseId}/unlock-edit`,
        { reason, notes },
        { headers: authHeaders() }
      );
      setCourse(data);
      return data;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to unlock course';
      setError(msg);
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  }, [courseId]);

  const deleteCourse = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.delete(`${API}/api/learning/courses/${courseId}`, {
        headers: authHeaders(),
      });
      return data;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to delete course';
      setError(msg);
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  }, [courseId]);

  return {
    course,
    loading,
    saving,
    error,
    refresh,
    saveCourse,
    publishCourse,
    unpublishCourse,
    unlockCourseForEdit,
    deleteCourse,
  };
}

export async function createLearningCourse(payload) {
  const { data } = await axios.post(`${API}/api/learning/courses`, payload, {
    headers: authHeaders(),
  });
  return data;
}

export async function fetchPublishedCourses() {
  const { data } = await axios.get(`${API}/api/learning/courses`, {
    headers: authHeaders(),
    params: { status: 'published' },
  });
  return data.items || [];
}

export async function uploadLearningMedia(courseId, assetType, file, lessonId) {
  const form = new FormData();
  form.append('asset_type', assetType);
  form.append('file', file);
  if (lessonId) form.append('lesson_id', lessonId);
  const { data } = await axios.post(`${API}/api/learning/courses/${courseId}/media`, form, {
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function previewCourseCertificate(courseId, certificateConfig) {
  const { data } = await axios.post(
    `${API}/api/learning/courses/${courseId}/certificate-preview`,
    { certificate_config: certificateConfig },
    { headers: authHeaders(), responseType: 'blob' }
  );
  return data;
}

export async function fetchLearningMedia({ course_id, asset_type, q, limit = 48, offset = 0 }) {
  const { data } = await axios.get(`${API}/api/learning/media`, {
    headers: authHeaders(),
    params: { course_id, asset_type, q, limit, offset },
  });
  return data;
}

export async function duplicateLearningSection(courseId, sectionId, fromCourseId) {
  const { data } = await axios.post(
    `${API}/api/learning/courses/${courseId}/sections/${sectionId}/duplicate`,
    {},
    { headers: authHeaders(), params: fromCourseId ? { from_course_id: fromCourseId } : {} }
  );
  return data;
}

export async function fetchCourseStats(courseId) {
  const { data } = await axios.get(`${API}/api/learning/courses/${courseId}/stats`, {
    headers: authHeaders(),
  });
  return data;
}

export async function fetchCourseTrainingMatrix(courseId) {
  const { data } = await axios.get(`${API}/api/learning/courses/${courseId}/training-matrix`, {
    headers: authHeaders(),
  });
  return data;
}

export async function fetchCourseEndorsement(courseId) {
  const { data } = await axios.get(`${API}/api/learning/courses/${courseId}/endorsement`, {
    headers: authHeaders(),
  });
  return data;
}

export async function fetchEndorsementReport(courseId) {
  const { data } = await axios.get(`${API}/api/learning/courses/${courseId}/endorsement-report`, {
    headers: authHeaders(),
  });
  return data;
}

export async function createEndorsementRequest(courseId, payload) {
  const { data } = await axios.post(
    `${API}/api/learning/courses/${courseId}/endorsement-requests`,
    payload,
    { headers: authHeaders() }
  );
  return data;
}

export async function replyToEndorsementComment(commentId, body) {
  const { data } = await axios.post(
    `${API}/api/learning/endorsement-comments/${commentId}/reply`,
    { body },
    { headers: authHeaders() }
  );
  return data;
}

export async function resolveEndorsementComment(commentId, status, note) {
  const { data } = await axios.patch(
    `${API}/api/learning/endorsement-comments/${commentId}/resolve`,
    { status, note },
    { headers: authHeaders() }
  );
  return data;
}

export async function sendEndorsementRereview(courseId, requestId) {
  const { data } = await axios.post(
    `${API}/api/learning/courses/${courseId}/endorsement-requests/${requestId}/rereview`,
    {},
    { headers: authHeaders() }
  );
  return data;
}

export async function recallEndorsementRequest(courseId, requestId) {
  const { data } = await axios.post(
    `${API}/api/learning/courses/${courseId}/endorsement-requests/${requestId}/recall`,
    {},
    { headers: authHeaders() }
  );
  return data;
}

export async function startEndorsementFixSession(courseId, requestId) {
  const { data } = await axios.post(
    `${API}/api/learning/courses/${courseId}/endorsement-requests/${requestId}/start-fix-session`,
    {},
    { headers: authHeaders() }
  );
  return data;
}

export async function fetchEndorsementFixWorkspace(courseId, requestId) {
  const { data } = await axios.get(
    `${API}/api/learning/courses/${courseId}/endorsement-requests/${requestId}/fix-workspace`,
    { headers: authHeaders() }
  );
  return data;
}

export async function saveEndorsementAdminCommentResponse(commentId, payload) {
  const { data } = await axios.patch(
    `${API}/api/learning/endorsement-comments/${commentId}/admin-response`,
    payload,
    { headers: authHeaders() }
  );
  return data;
}

export async function sendEndorsementFixSummary(courseId, requestId, payload) {
  const { data } = await axios.post(
    `${API}/api/learning/courses/${courseId}/endorsement-requests/${requestId}/send-fix-summary`,
    payload,
    { headers: authHeaders() }
  );
  return data;
}

export async function fetchCourseLearnerFeedback(courseId) {
  const { data } = await axios.get(`${API}/api/learning/courses/${courseId}/learner-feedback`, {
    headers: authHeaders(),
  });
  return data;
}

export async function submitLearnerFeedback(enrollmentId, payload) {
  const { data } = await axios.post(
    `${API}/api/learning/enrollments/${enrollmentId}/feedback`,
    payload,
    { headers: authHeaders() }
  );
  return data;
}

export async function downloadEndorsementReportPdf(courseId) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API}/api/learning/courses/${courseId}/endorsement-report/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('PDF download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'endorsement-report.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadEndorsementReportExcel(courseId) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API}/api/learning/courses/${courseId}/endorsement-report/excel`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Excel download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'endorsement-report.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchTrainingMatrix({ scope = 'enrolled', courseIds } = {}) {
  const { data } = await axios.get(`${API}/api/learning/reports/training-matrix`, {
    headers: authHeaders(),
    params: { scope, course_ids: courseIds },
  });
  return data;
}

export async function downloadTrainingMatrixPdf({ scope = 'enrolled', courseIds } = {}) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(
    `${API}/api/learning/reports/training-matrix/pdf?scope=${encodeURIComponent(scope)}${
      courseIds ? `&course_ids=${encodeURIComponent(courseIds)}` : ''
    }`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  );
  if (!res.ok) throw new Error('PDF download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'training-matrix.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadTrainingMatrixExcel({ scope = 'enrolled', courseIds } = {}) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(
    `${API}/api/learning/reports/training-matrix/excel?scope=${encodeURIComponent(scope)}${
      courseIds ? `&course_ids=${encodeURIComponent(courseIds)}` : ''
    }`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  );
  if (!res.ok) throw new Error('Excel download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'training-matrix.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}

export async function submitLessonDiscussion(enrollmentId, lessonId, body) {
  const { data } = await axios.post(
    `${API}/api/learning/enrollments/${enrollmentId}/lessons/${lessonId}/discussion`,
    { body },
    { headers: authHeaders() }
  );
  return data;
}

export async function submitLearnerUpload(enrollmentId, lessonId, file) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(
    `${API}/api/learning/enrollments/${enrollmentId}/lessons/${lessonId}/upload`,
    form,
    { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function submitQuestionAnswer(enrollmentId, lessonId, answer) {
  const { data } = await axios.post(
    `${API}/api/learning/enrollments/${enrollmentId}/lessons/${lessonId}/question-answer`,
    { answer },
    { headers: authHeaders() }
  );
  return data;
}

export async function fetchLessonInteractions(enrollmentId, lessonId) {
  const { data } = await axios.get(
    `${API}/api/learning/enrollments/${enrollmentId}/lessons/${lessonId}/interactions`,
    { headers: authHeaders() }
  );
  return data.items || [];
}

export function learningStreamUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const token = localStorage.getItem('access_token');
  const base = path.startsWith('/') ? `${API}${path}` : `${API}/${path}`;
  return token ? `${base}?access_token=${encodeURIComponent(token)}` : base;
}

export function useMyEnrollments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/learning/enrollments/my`, {
        headers: authHeaders(),
      });
      setItems(data.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load enrollments');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, loading, error, refresh };
}

export function useLearnerCatalog(categoryId = null) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (categoryId) params.category_id = categoryId;
      const { data } = await axios.get(`${API}/api/learning/catalog`, {
        headers: authHeaders(),
        params,
      });
      setItems(data.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load catalog');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, loading, error, refresh };
}

export async function selfEnrollFromCatalog(courseId) {
  const { data } = await axios.post(
    `${API}/api/learning/catalog/${courseId}/enroll`,
    {},
    { headers: authHeaders() }
  );
  return data;
}

export async function startEnrollmentV2Run({ enrollmentId, courseId }) {
  if (enrollmentId) {
    const { data } = await axios.post(
      `${API}/api/learning/enrollments/${enrollmentId}/start-v2-run`,
      {},
      { headers: authHeaders() }
    );
    return data;
  }
  if (!courseId) throw new Error('courseId or enrollmentId required');
  const { data } = await axios.post(
    `${API}/api/learning/catalog/${courseId}/start-v2-run`,
    {},
    { headers: authHeaders() }
  );
  return data;
}

export async function fetchCertificatePdfBlob(certificateId) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API}/api/learning/certificates/${certificateId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Certificate download failed');
  }
  return res.blob();
}

export function useCourseEnrollments(courseId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(courseId));
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/learning/courses/${courseId}/enrollments`, {
        headers: authHeaders(),
      });
      setItems(data.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load enrollments');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, loading, error, refresh };
}

export function useEmployeeEnrollments(employeeId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(employeeId));
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/learning/employees/${employeeId}/enrollments`, {
        headers: authHeaders(),
      });
      setItems(data.items || []);
    } catch (err) {
      if (err?.response?.status === 404) {
        const courses = await fetchPublishedCourses();
        const all = await Promise.all(
          courses.map(async (c) => {
            try {
              const { data: enrData } = await axios.get(
                `${API}/api/learning/courses/${c.id}/enrollments`,
                { headers: authHeaders() }
              );
              return (enrData.items || []).filter((e) => e.employee_id === employeeId);
            } catch {
              return [];
            }
          })
        );
        const flat = all.flat();
        setItems(flat);
        setError(null);
      } else {
        setError(err?.response?.data?.detail || err.message || 'Failed to load enrollments');
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, loading, error, refresh };
}

export function usePendingReviewEnrollments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/learning/enrollments/pending-review`, {
        headers: authHeaders(),
      });
      setItems(data.items || []);
    } catch (err) {
      if (err?.response?.status === 404) {
        const courses = await axios.get(`${API}/api/learning/courses`, {
          headers: authHeaders(),
        });
        const courseList = courses.data.items || [];
        const all = await Promise.all(
          courseList.map(async (c) => {
            try {
              const { data: enrData } = await axios.get(
                `${API}/api/learning/courses/${c.id}/enrollments`,
                { headers: authHeaders() }
              );
              return (enrData.items || [])
                .filter((e) => e.status === 'pending_review')
                .map((e) => ({ ...e, course: c }));
            } catch {
              return [];
            }
          })
        );
        setItems(all.flat());
        setError(null);
      } else {
        setError(err?.response?.data?.detail || err.message || 'Failed to load pending reviews');
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, loading, error, refresh };
}

export function useLearningPlayer(enrollmentId) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(Boolean(enrollmentId));
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!enrollmentId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/learning/enrollments/${enrollmentId}/player`,
        { headers: authHeaders() }
      );
      setPayload(data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveProgress = useCallback(
    async (body) => {
      await axios.post(`${API}/api/learning/enrollments/${enrollmentId}/progress`, body, {
        headers: authHeaders(),
      });
      await refresh();
    },
    [enrollmentId, refresh]
  );

  const submitQuiz = useCallback(
    async (lessonId, answers) => {
      const { data } = await axios.post(
        `${API}/api/learning/enrollments/${enrollmentId}/quiz-submit`,
        { lesson_id: lessonId, answers },
        { headers: authHeaders() }
      );
      await refresh();
      return data;
    },
    [enrollmentId, refresh]
  );

  const completeCourse = useCallback(async () => {
    const { data } = await axios.post(
      `${API}/api/learning/enrollments/${enrollmentId}/complete`,
      {},
      { headers: authHeaders() }
    );
    return data;
  }, [enrollmentId]);

  return { payload, loading, error, refresh, saveProgress, submitQuiz, completeCourse };
}

export async function enrollEmployee(payload) {
  const { data } = await axios.post(`${API}/api/learning/enrollments`, payload, {
    headers: authHeaders(),
  });
  return data;
}

export async function bulkEnrollEmployees(payload) {
  const { data } = await axios.post(`${API}/api/learning/enrollments/bulk`, payload, {
    headers: authHeaders(),
  });
  return data;
}

export async function getEnrollmentDetail(enrollmentId) {
  const { data } = await axios.get(`${API}/api/learning/enrollments/${enrollmentId}`, {
    headers: authHeaders(),
  });
  return data;
}

export async function patchEnrollment(enrollmentId, payload) {
  const { data } = await axios.patch(`${API}/api/learning/enrollments/${enrollmentId}`, payload, {
    headers: authHeaders(),
  });
  return data;
}

export async function approveEnrollment(enrollmentId) {
  const { data } = await axios.post(
    `${API}/api/learning/enrollments/${enrollmentId}/approve`,
    {},
    { headers: authHeaders() }
  );
  return data;
}

export async function rejectEnrollment(enrollmentId, reason) {
  const { data } = await axios.post(
    `${API}/api/learning/enrollments/${enrollmentId}/reject`,
    { reason },
    { headers: authHeaders() }
  );
  return data;
}

export async function revokeEnrollment(enrollmentId, reason) {
  const { data } = await axios.post(
    `${API}/api/learning/enrollments/${enrollmentId}/revoke`,
    { reason },
    { headers: authHeaders() }
  );
  return data;
}

export async function reassignEnrollment(enrollmentId, payload) {
  const { data } = await axios.post(
    `${API}/api/learning/enrollments/${enrollmentId}/reassign`,
    payload,
    { headers: authHeaders() }
  );
  return data;
}

export async function resetEnrollmentProgress(enrollmentId) {
  const { data } = await axios.post(
    `${API}/api/learning/enrollments/${enrollmentId}/reset-progress`,
    {},
    { headers: authHeaders() }
  );
  return data;
}

export async function cancelEnrollment(enrollmentId) {
  const { data } = await axios.delete(`${API}/api/learning/enrollments/${enrollmentId}`, {
    headers: authHeaders(),
  });
  return data;
}

export async function downloadEnrollmentCompletionReport(enrollmentId) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(
    `${API}/api/learning/enrollments/${enrollmentId}/completion-report/pdf`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Completion report download failed');
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] || 'completion-report.pdf';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchAssignmentRules() {
  const { data } = await axios.get(`${API}/api/learning/assignment-rules`, {
    headers: authHeaders(),
  });
  return data.items || [];
}

export async function createAssignmentRule(payload) {
  const { data } = await axios.post(`${API}/api/learning/assignment-rules`, payload, {
    headers: authHeaders(),
  });
  return data;
}

export async function updateAssignmentRule(ruleId, payload) {
  const { data } = await axios.patch(`${API}/api/learning/assignment-rules/${ruleId}`, payload, {
    headers: authHeaders(),
  });
  return data;
}

export async function deleteAssignmentRule(ruleId) {
  const { data } = await axios.delete(`${API}/api/learning/assignment-rules/${ruleId}`, {
    headers: authHeaders(),
  });
  return data;
}

export async function fetchEmployeeEligibility(employeeId, context = 'shift_assign') {
  const { data } = await axios.get(`${API}/api/learning/eligibility/${employeeId}`, {
    headers: authHeaders(),
    params: { context },
  });
  return data;
}

/** Self-service learning blockers — eligibility API with enrollments fallback. */
export async function fetchMyLearningEligibility(context = 'login') {
  const headers = authHeaders();
  try {
    const meRes = await axios.get(`${API}/api/employees/me`, { headers });
    const employeeId = meRes.data?.id;
    if (!employeeId) return { blockers: [], warnings: [] };

    try {
      const { data } = await axios.get(`${API}/api/learning/eligibility/${employeeId}`, {
        headers,
        params: { context },
      });
      return data;
    } catch {
      const { data } = await axios.get(`${API}/api/learning/enrollments/my`, { headers });
      const items = data.items || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const blockers = items
        .filter((enr) => enr.is_compliance_required && !['completed', 'cancelled'].includes(enr.status))
        .filter((enr) => {
          if (enr.status === 'overdue') return true;
          if (enr.due_date) {
            const due = new Date(enr.due_date);
            due.setHours(0, 0, 0, 0);
            return due < today;
          }
          return false;
        })
        .map((enr) => ({
          code: 'learning_overdue',
          message: (enr.course?.title || enr.linked_doc_type_name || 'Required training'),
          source: 'learning_enrollment',
          ref_id: enr.id,
        }));
      return { blockers, warnings: [] };
    }
  } catch {
    return { blockers: [], warnings: [] };
  }
}

export async function fetchLearningSettings() {
  const { data } = await axios.get(`${API}/api/learning/settings`, {
    headers: authHeaders(),
  });
  return data;
}

export async function updateLearningSettings(payload) {
  const { data } = await axios.put(`${API}/api/learning/settings`, payload, {
    headers: authHeaders(),
  });
  return data;
}

export async function fetchLearningUsage() {
  try {
    const { data } = await axios.get(`${API}/api/learning/usage`, {
      headers: authHeaders(),
    });
    return {
      storage_gb: data?.learning_assets_gb ?? null,
      storage_limit_gb: data?.company_storage_usage?.limit_gb ?? null,
      emails_used: undefined,
      emails_limit: undefined,
      push_used: undefined,
      push_limit: undefined,
      learning_assets_bytes: data?.learning_assets_bytes,
      by_asset_type: data?.by_asset_type,
      company_storage_usage: data?.company_storage_usage,
    };
  } catch {
    const { data } = await axios.get(`${API}/api/storage/usage`, {
      headers: authHeaders(),
    });
    return {
      storage_gb: data?.used_gb ?? data?.used_bytes ? (data.used_bytes / 1e9).toFixed(1) : null,
      storage_limit_gb: data?.limit_gb ?? data?.quota_gb,
      emails_used: data?.learning_emails_used,
      emails_limit: data?.learning_emails_limit,
      push_used: data?.learning_push_used,
      push_limit: data?.learning_push_limit,
    };
  }
}

export function useLearningCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/learning/categories`, {
        headers: authHeaders(),
        params: { include_counts: true },
      });
      setCategories(data.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { categories, loading, error, refresh };
}

export function useGamificationProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/learning/gamification/profile`, { headers: authHeaders() })
      .then(({ data }) => setProfile(data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, []);

  return { profile, loading };
}
